import React from 'react';
import { View } from 'react-native';

import Colors from '../theming/Colors';
import KitIcon, { IconName } from './KitIcon';

export type KitContainedIconColor = 'default' | 'blue' | 'green';
export type KitContainedIconSize = 'xs' | 'sm' | 'md' | 'lg';
export type KitContainedIconSet = 'styled' | 'unstyled';

// Color mapping to match Waves ContainedIcon
const colorMap: Record<KitContainedIconColor, { bg: string; icon: string }> = {
  default: { bg: Colors.N75, icon: Colors.N900 },
  blue: { bg: Colors.S100, icon: Colors.brand },
  green: { bg: Colors.G50, icon: Colors.G700 },
};

// Size mapping to match Waves ContainedIcon exactly
const sizeMap: Record<
  KitContainedIconSize,
  { container: number; icon: number }
> = {
  xs: { container: 24, icon: 14 },
  sm: { container: 48, icon: 20 },
  md: { container: 64, icon: 30 },
  lg: { container: 96, icon: 40 },
};

interface KitContainedIconProps {
  // The color theme of the contained icon.
  color?: KitContainedIconColor;
  // Rendered size of the contained icon.
  size: KitContainedIconSize;
  // The name of the icon to render.
  name: IconName;
  /**
   * The icon set to use (no-op for native for now, only needed for API parity with Waves).
   * In the future, this will be used to switch between styled/unstyled icon sets.
   * styled: for SVGs that have a predefined fill color or graphic that should not be colored/tinted by the component
   * unstyled: for SVGs that have no fill color that should be colored by the component
   */
  set?: KitContainedIconSet;
}

// A standardized contained icon component for parity with Contained Icon
// from Waves: https://waves.subsplash.net/?path=/docs/components-icons--page
function KitContainedIcon(props: KitContainedIconProps): JSX.Element {
  const { color = 'default', size, name } = props;

  const containerSize = sizeMap[size].container;
  const iconSize = sizeMap[size].icon;
  const backgroundColor = colorMap[color].bg;
  const iconColor = colorMap[color].icon;

  return (
    <View
      style={[
        {
          alignItems: 'center',
          backgroundColor,
          borderRadius: containerSize / 2,
          height: containerSize,
          justifyContent: 'center',
          width: containerSize,
        },
      ]}
    >
      <KitIcon name={name} size={iconSize} color={iconColor} />
    </View>
  );
}

export default KitContainedIcon;
